import { Helmet } from 'react-helmet';
import React from 'react';
import { useAuth } from '../../auth/firebaseAuth';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Camera, FileText, ArrowRight } from 'lucide-react';

const SellerChecklistPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const checklistItems = [
    {
      icon: <FileText className="h-6 w-6 text-blue-600" />,
      title: 'BER Certificate',
      description:
        "You need a valid Building Energy Rating (BER) certificate to sell your home. If you don't have one, you'll need to arrange an assessment."
    },
    {
      icon: <Camera className="h-6 w-6 text-blue-600" />,
      title: 'Professional Photos',
      description:
        'High-quality photos are key to attracting buyers. Clean and declutter your home before taking pictures.'
    },
    {
      icon: <CheckCircle2 className="h-6 w-6 text-blue-600" />,
      title: 'Property Documents',
      description:
        'Gather necessary documents like deeds, proof of identity, and property tax records to speed up the sale process.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>FSBO Seller Legal Checklist | Sell Your Home in Ireland | RentSmart.ie</title>
        <meta
          name="description"
          content="Step-by-step legal checklist for For Sale By Owner (FSBO) sellers in Ireland. Ensure you have all documents and requirements to sell your home smoothly. Download, print, and track your progress with RentSmart.ie."
        />
        <meta
          name="keywords"
          content="FSBO, seller checklist, legal checklist, sell home Ireland, property sale, title deeds, BER certificate, solicitor, property tax, planning documents, RentSmart.ie"
        />
        <link rel="canonical" href="https://rentsmart.ie/seller-checklist" />
        <meta property="og:title" content="FSBO Seller Legal Checklist | RentSmart.ie" />
        <meta
          property="og:description"
          content="Step-by-step legal checklist for FSBO sellers in Ireland. Download, print, and track your property sale progress with RentSmart.ie."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/seller-checklist" />
      </Helmet>
      <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible min-h-screen  bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4 space-x-3">
              <h1 className="text-4xl font-bold text-gray-900">Ready to sell your home?</h1>
              <span className="inline-flex items-center bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                For Sale By Owner
              </span>
            </div>
            <p className="text-sm text-gray-600 max-w-2xl mx-auto mb-4">
              This page is for homeowners selling directly. RentSmart provides an advertising
              platform to list and promote your property — we are not acting as a selling agent.
            </p>

            <p className="text-lg text-gray-600">
              Here are a few things to have ready before you list your property.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="p-6 md:p-8">
              <div className="space-y-8">
                {checklistItems.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg">{item.icon}</div>
                    <div className="ml-4">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 md:px-8 md:py-6 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-gray-500">Got everything ready?</p>
                <button
                  onClick={() => {
                    if (currentUser) {
                      navigate('/dashboard/fsbo');
                    } else {
                      navigate('/signup?returnUrl=/dashboard/fsbo');
                    }
                  }}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  {currentUser ? 'Go to your dashboard' : 'Create your account'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellerChecklistPage;
